/**
 * ReviewStoryPage — post-publish surfacing step.
 *
 * After km_process_story runs, the story is already live with AI-suggested
 * dimensions + highlight. This screen lets the author react: keep/remove
 * dimension chips, edit the highlight. Saving calls km_update_story.
 *
 * Reachable two ways:
 *   - Auto-redirected from /submit after publish (?from=submit hides the
 *     "Cancel" affordance and tunes copy to "Help readers find it").
 *   - From the edit flow via an "Edit themes & highlight" link, when the
 *     author wants to revisit the metadata without changing the body.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import PageHeader, { ToolbarCaptionButton } from '@/components/PageHeader';
import ReviewStoryPanel, { HIGHLIGHT_MAX } from '@/components/ReviewStoryPanel';
import { useAuth } from '@/context/AuthContext';
import { useTagOptions } from '@/hooks/useTagOptions';
import { getMyStoryForEdit } from '@/services/storyRepo';
import { reanalyzeStory, updateStory } from '@/services/storyService';
import { FILTER_DIMENSIONS } from '@/lib/filters';

// Every editable dimension, including practices — the review panel now
// surfaces practice chips so authors can curate AI's picks here too.
const DIMENSION_KEYS = FILTER_DIMENSIONS.map((d) => d.key);

const buildSelectedFromStory = (story) =>
  DIMENSION_KEYS.reduce((acc, key) => {
    acc[key] = new Set(story?.[key] || []);
    return acc;
  }, {});

// Truncated preview blurb for the read-only "story preview" panel on this
// page. Not the share-card excerpt — this is just orientation for the author.
const previewBody = (story) => {
  if (!story) return '';
  if (story.raw_text) return story.raw_text;
  const sections = story.sections || [];
  return sections.map((s) => s.content).filter(Boolean).join('\n\n');
};

const ReviewStoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromSubmit = searchParams.get('from') === 'submit';
  const { user, loading: authLoading } = useAuth();
  const { tagOptions, loading: tagsLoading } = useTagOptions();

  const [story, setStory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState(() => buildSelectedFromStory(null));
  const [highlight, setHighlight] = useState('');
  const [saving, setSaving] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  // A fresh highlight from re-analyze that differs from the author's
  // current draft. We never overwrite their text silently — it's parked
  // here and surfaced as a "Use this / Keep mine" choice in the panel.
  const [suggestedHighlight, setSuggestedHighlight] = useState(null);

  const userId = user?.id;
  const exitPath = `/story/${id}`;

  useEffect(() => {
    if (!authLoading && !userId) {
      toast.error('Please sign in to review your story');
      navigate('/login');
    }
  }, [authLoading, userId, navigate]);

  useEffect(() => {
    if (!id || authLoading || !userId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const fetched = await getMyStoryForEdit(id);
        if (cancelled) return;
        if (!fetched) {
          toast.error('Story not found or you do not have access');
          navigate('/my-stories', { replace: true });
          return;
        }
        setStory(fetched);
        setSelected(buildSelectedFromStory(fetched));
        setHighlight(fetched.highlight || '');
      } catch (err) {
        if (cancelled) return;
        console.error('Review load error:', err);
        setLoadError(err?.message || 'Could not load this story');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, userId, authLoading, navigate]);

  const handleToggleTag = (dimensionKey, tagId) => {
    setSelected((prev) => {
      const next = { ...prev };
      const set = new Set(next[dimensionKey] || []);
      if (set.has(tagId)) set.delete(tagId);
      else set.add(tagId);
      next[dimensionKey] = set;
      return next;
    });
  };

  const handleSave = async () => {
    if (!id || saving) return;
    setSaving(true);
    try {
      const updates = {
        highlight: (highlight || '').slice(0, HIGHLIGHT_MAX).trim(),
      };
      for (const key of DIMENSION_KEYS) {
        updates[key] = Array.from(selected[key] || []);
      }
      await updateStory(id, updates);
      toast.success('Saved.');
      navigate(exitPath);
    } catch (err) {
      console.error('Review save error:', err);
      toast.error(err?.message || 'Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => navigate(exitPath);

  const handleUseSuggestedHighlight = () => {
    if (!suggestedHighlight) return;
    setHighlight(suggestedHighlight);
    setSuggestedHighlight(null);
  };

  const handleDismissSuggestedHighlight = () => setSuggestedHighlight(null);

  const handleReanalyze = async () => {
    if (!id || reanalyzing || saving) return;
    setReanalyzing(true);
    try {
      const result = await reanalyzeStory(id);
      const s = result?.suggestions || {};
      // Per-dimension merge strategy:
      //   • guna_arc — REPLACE with the LLM's arc when one is returned.
      //     guna_arc is an ordered trajectory (tamas → rajas → sattva,
      //     etc.), so Set-merging both ignores ordering and never lets
      //     a stale arc get corrected. If LLM returns nothing, keep
      //     whatever the user has now.
      //   • Everything else — MERGE: union of current selection +
      //     LLM suggestions. Preserves any manual additions the user
      //     already made.
      const merged = {};
      let added = 0;
      let gunaReset = false;
      for (const key of DIMENSION_KEYS) {
        const incoming = s[key] || [];
        if (key === 'guna_arc') {
          if (incoming.length > 0) {
            const before = Array.from(selected[key] || []).join(',');
            const after = incoming.join(',');
            if (before !== after) gunaReset = true;
            merged[key] = new Set(incoming);
          } else {
            merged[key] = new Set(selected[key] || []);
          }
          continue;
        }
        const next = new Set(selected[key] || []);
        for (const tagId of incoming) {
          if (!next.has(tagId)) {
            next.add(tagId);
            added += 1;
          }
        }
        merged[key] = next;
      }
      setSelected(merged);

      // Highlight is a scalar the author may have already worded carefully,
      // so we never overwrite it silently. When the LLM returns one that
      // differs from the current draft, park it as a suggestion the author
      // can apply or dismiss (rendered under the textarea). Identical text
      // (or none) clears any stale suggestion.
      const incomingHighlight = (s.highlight || '').trim();
      const currentHighlight = (highlight || '').trim();
      const highlightSuggested =
        !!incomingHighlight && incomingHighlight !== currentHighlight;
      setSuggestedHighlight(highlightSuggested ? incomingHighlight : null);

      // Applied changes (themes, energy arc) read as "Updated …"; the
      // highlight is only *offered*, so it gets its own clause.
      const appliedParts = [];
      if (added > 0) {
        appliedParts.push(`${added} new theme${added === 1 ? '' : 's'}`);
      }
      if (gunaReset) appliedParts.push('energy arc');

      const segments = [];
      if (appliedParts.length > 0) {
        // Natural-language join: "X", "X and Y", "X, Y and Z".
        const joined = appliedParts.length === 1
          ? appliedParts[0]
          : `${appliedParts.slice(0, -1).join(', ')} and ${appliedParts[appliedParts.length - 1]}`;
        segments.push(`Updated ${joined}`);
      }
      if (highlightSuggested) {
        segments.push(
          segments.length > 0
            ? 'and suggested a new highlight below'
            : 'Suggested a new highlight below'
        );
      }

      if (segments.length === 0) {
        toast.success('Nothing new to add — your selections already cover it.');
      } else {
        toast.success(`${segments.join(' ')}.`);
      }
    } catch (err) {
      console.error('Re-analyze error:', err);
      toast.error(err?.message || 'Could not re-analyze right now.');
    } finally {
      setReanalyzing(false);
    }
  };

  const showSpinner = authLoading || loading || tagsLoading;
  const body = useMemo(() => previewBody(story), [story]);
  const livePreview = (highlight || '').trim();

  if (showSpinner) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#C05621]" />
          <p className="mt-4 text-[#6B665E]">Loading review…</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-4 text-center">
        <div>
          <p className="text-2xl text-[#2D2A26] font-serif">Could not load this story</p>
          <p className="text-sm text-[#6B665E] mt-2 break-words">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!story) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <PageHeader>
        <ToolbarCaptionButton
          label={fromSubmit ? 'Skip to story' : 'Back to story'}
          shortLabel={fromSubmit ? 'Skip' : 'Story'}
          onClick={() => navigate(exitPath)}
          data-testid="review-back-btn"
        />
      </PageHeader>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="text-center mb-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-serif tracking-tight leading-tight text-[#2D2A26] mb-3">
            {fromSubmit ? 'Your story is live' : 'Edit themes & highlight'}
          </h2>
          <p className="text-base md:text-lg leading-relaxed text-[#6B665E] max-w-2xl mx-auto">
            {fromSubmit
              ? 'Help the right readers find it — review the themes we picked up and tune the short hook.'
              : 'These shape how your story surfaces in feeds and search.'}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <aside
            className="bg-white rounded-xl border border-[#E6E2D6] p-6 space-y-4 self-start lg:sticky lg:top-24"
            data-testid="review-story-preview"
          >
            <div className="text-xs uppercase tracking-wider text-[#6B665E]">Preview</div>
            <h3 className="text-2xl font-serif text-[#2D2A26] leading-snug">
              {story.title || 'Untitled'}
            </h3>
            {livePreview && (
              <p
                className="text-[#2D2A26] leading-relaxed border-l-4 border-[#C05621] pl-4 italic"
                data-testid="review-live-highlight"
              >
                {livePreview}
              </p>
            )}
            {body && (
              <p className="text-sm text-[#6B665E] leading-relaxed whitespace-pre-wrap line-clamp-[12]">
                {body}
              </p>
            )}
          </aside>

          <div>
            <ReviewStoryPanel
              story={story}
              selected={selected}
              tagOptions={tagOptions}
              highlight={highlight}
              suggestedHighlight={suggestedHighlight}
              onToggleTag={handleToggleTag}
              onHighlightChange={setHighlight}
              onUseSuggestedHighlight={handleUseSuggestedHighlight}
              onDismissSuggestedHighlight={handleDismissSuggestedHighlight}
              saving={saving}
              onSave={handleSave}
              onSkip={fromSubmit ? handleSkip : undefined}
              onReanalyze={handleReanalyze}
              reanalyzing={reanalyzing}
              saveLabel={fromSubmit ? 'Save & finish' : 'Save changes'}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStoryPage;
